import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { CloseSmall } from '@wix/wix-ui-icons-common/on-stage';
import { useStyles } from '@wix/tpa-settings/react';
import { IconButton, Tag, TagSkin } from '@wix/wix-ui-tpa';

import {
  selectMessages,
  selectLayout,
  expandWidget,
  setRead as setReadThunk,
  getMessagesHistory,
  messagesQueryBuilder,
  selectOneChatComposed,
  selectOneChatSettings,
  addMessage,
} from '@/features';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  EXPERIMENTS,
  SS_ENGAGE_MESSAGE_SHOWN,
  SS_SUGGESTION_CHIPS_SHOWN,
} from '@/constants';
import { useOneChatIntroData, useSessionStorage } from '@/hooks';

import {
  icons,
  iconsDict,
  SettingsMinimizedButtonPlayAnimation,
} from 'AiAssistantWidget/Settings/components';
import settingsParams from 'AiAssistantWidget/settingsParams';
import {
  useRootProps,
  useWidgetSettings,
} from 'AiAssistantWidget/Widget/contexts';
import styleParams from 'AiAssistantWidget/stylesParams';
import { useAppExperiments } from 'AiAssistantWidget/experiments';

import { NotificationBubble } from '../NotificationBubble';

import { MinimizedChatDataHooks } from './MinimizedChatDataHooks';
import { st, classes } from './MinimizedChat.st.css';

type PlayAnimationState = Record<
  SettingsMinimizedButtonPlayAnimation,
  boolean
> & {
  hovePreview: boolean;
};

const CANCEL_ANIMATION_TIMEOUT = 5000;

export const MinimizedChat = ({
  setRef,
}: {
  setRef: (ref: HTMLDivElement | null) => void;
}) => {
  const { t } = useTranslation();
  const { direction } = useRootProps();
  const styles = useStyles();
  const settings = useWidgetSettings();
  const { experiments } = useAppExperiments();
  const dispatch = useAppDispatch();
  const { isMobile, isEditor } = useEnvironment();
  const lastUnread = useAppSelector(selectMessages.lastUnread);
  const totalMessages = useAppSelector(selectMessages.total);
  const chipsExperimentEnabled = experiments.enabled(EXPERIMENTS.INTRO_CHIPS);
  const isAi = useAppSelector(selectOneChatSettings.isAi);

  const isOnline = useAppSelector(selectOneChatComposed.maybeWidgetOnline);

  const shouldLoadMessages = useAppSelector(selectMessages.shouldLoadMessages);
  const wasOpened = useAppSelector(selectLayout.wasOpened);

  const engageMessage = useAppSelector(selectOneChatComposed.engageMessage);
  const shouldShowSuggestionChips = useAppSelector(
    selectOneChatSettings.shouldShowSuggestionChips,
  );

  const shouldAutoOpen = useAppSelector(
    selectOneChatComposed.shouldAutoOpenWidget,
  );

  const [engageMessageShown, setEngageMessageShown] = useSessionStorage(
    SS_ENGAGE_MESSAGE_SHOWN,
    false,
  );

  const [suggestionChipsShown, setSuggestionChipsShown] = useSessionStorage(
    SS_SUGGESTION_CHIPS_SHOWN,
    false,
  );

  const isOpened = useAppSelector(selectLayout.visual) === 'visible';
  const buttonRef = useRef<HTMLButtonElement>(null);

  const suggestions = useAppSelector(selectOneChatComposed.aiSuggestions);

  const shouldShowSuggestionChipsContainer = useMemo<boolean>(
    () =>
      !isEditor &&
      Boolean(suggestions?.length) &&
      !suggestionChipsShown &&
      shouldShowSuggestionChips &&
      chipsExperimentEnabled,
    [
      isEditor,
      chipsExperimentEnabled,
      shouldShowSuggestionChips,
      suggestionChipsShown,
      suggestions?.length,
    ],
  );

  const hasLastUnread = useMemo<boolean>(
    () => Boolean(lastUnread),
    [lastUnread],
  );

  const hasEngageMessage = useMemo<boolean>(
    () =>
      Boolean(engageMessage) &&
      !engageMessageShown &&
      !shouldAutoOpen &&
      !isEditor,
    [engageMessage, engageMessageShown, isEditor, shouldAutoOpen],
  );

  const expand = useCallback(() => {
    if (hasEngageMessage) {
      setEngageMessageShown(true);
    }

    if (suggestions && suggestions.length > 0) {
      setSuggestionChipsShown(true);
    }

    dispatch(expandWidget());
  }, [
    dispatch,
    hasEngageMessage,
    setEngageMessageShown,
    suggestions,
    setSuggestionChipsShown,
  ]);

  const setRead = useCallback(() => {
    dispatch(setReadThunk());
  }, [dispatch]);

  const { buttonTextOnline, buttonTextOffline } = useMemo(
    () => ({
      buttonTextOnline: isAi
        ? settingsParams.textButtonOnline
        : settingsParams.manualChatTextButtonOnline,
      buttonTextOffline: isAi
        ? settingsParams.textButtonOffline
        : settingsParams.manualChatTextButtonOffline,
    }),
    [isAi],
  );
  const buttonText = useMemo(
    () => settings.get(isOnline ? buttonTextOnline : buttonTextOffline),
    [settings, isOnline, buttonTextOnline, buttonTextOffline],
  );

  const {
    readiness: { hasAnyIntro },
    isSequenceFulfilled,
    addCustomMessage,
  } = useOneChatIntroData();

  const onClickChip = useCallback(
    (question: string) => {
      if (totalMessages === 0 && hasAnyIntro && isSequenceFulfilled) {
        addCustomMessage(question);
      } else {
        dispatch(addMessage({ body: { mainText: question } }));
      }
      expand();
    },
    [
      dispatch,
      expand,
      hasAnyIntro,
      isSequenceFulfilled,
      addCustomMessage,
      totalMessages,
    ],
  );

  const [playAnimationStyleState, setPlayAnimationState] =
    useState<PlayAnimationState>({
      loop: false,
      load: false,
      hove: false,
      hovePreview: false,
    });

  const stylesConfig = useMemo(() => {
    let pos = 'floating';
    const settingsPos = settings.get(
      settingsParams.minimizedButtonPosition,
      'primary',
    );
    if (!isMobile && settingsPos) {
      pos = settingsPos;
    }

    let display = 'textIcon';
    const settingsDisplay = settings.get(
      settingsParams.minimizedButtonDisplay,
      'primary',
    );
    if (isMobile) {
      display = 'icon';
    } else if (settingsDisplay) {
      display = settingsDisplay;
    }

    const showBorderAnimation =
      settings.get(settingsParams.minimizedButtonBorderAnimation) &&
      styles.get(styleParams.minimizedButtonAnimatedBorderWidth) > 0;

    return {
      isLeft: settings.get(settingsParams.layout, 'primary') === 'left',
      isSticky: pos === 'sticky',
      showIcon: pos === 'icon' || display === 'icon' || display === 'textIcon',
      showText:
        pos !== 'icon' && (display === 'text' || display === 'textIcon'),
      hasShadow:
        settings.get(settingsParams.minimizedButtonShadow, 'primary') === true,
      icon:
        settings.get(settingsParams.minimizedButtonIcon, 'primary') ??
        icons[0].value,
      showBorderAnimation,
      playBorderAnimationType: settings.get(
        settingsParams.minimizedButtonPlayBorderAnimation,
      ),
      bubbleHasShadow:
        settings.get(settingsParams.bubbleNotificationShadow, 'primary') ===
        true,
    };
  }, [settings, styles, isMobile]);

  useEffect(() => {
    if (!isOpened && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, [isOpened]);

  useEffect(() => {
    const loop = stylesConfig.playBorderAnimationType === 'loop';
    const load = stylesConfig.playBorderAnimationType === 'load';
    const hove = stylesConfig.playBorderAnimationType === 'hove';

    setPlayAnimationState((p) => ({
      ...p,
      loop,
      load,
      hove,
      hovePreview: isEditor && hove,
    }));

    if (load) {
      setTimeout(() => {
        setPlayAnimationState((p) => ({ ...p, load: false }));
      }, CANCEL_ANIMATION_TIMEOUT);
    }
  }, [stylesConfig, isEditor]);

  useEffect(() => {
    if (wasOpened && shouldLoadMessages) {
      if (shouldLoadMessages) {
        dispatch(getMessagesHistory(messagesQueryBuilder()));
      }
    }
  }, [wasOpened, shouldLoadMessages, dispatch]);

  const shouldShowPopover = (hasLastUnread || hasEngageMessage) && !isMobile;

  const textContent = useMemo(() => {
    if (lastUnread?.body === undefined) {
      return '';
    }

    const { mainText, additionalData } = lastUnread.body;

    if (mainText) {
      return mainText;
    }

    if (additionalData?.[0].fileName) {
      return t('app.widget.bubbleMessage.fileWithName', {
        fileName: additionalData?.[0].fileName,
      });
    }

    if (additionalData?.[0].type === 'media') {
      return t('app.widget.bubbleMessage.fileWithoutName');
    }

    return '';
  }, [lastUnread, t]);

  const unreadBubbleProps = useMemo(
    () => ({
      minWidth: 220,
      textContent,
      onClick: expand,
      onClose: setRead,
    }),
    [textContent, expand, setRead],
  );

  const engageBubbleProps = useMemo(
    () => ({
      minWidth: 150,
      textContent: engageMessage || '',
      onClick: expand,
      onClose: () => {
        setEngageMessageShown(true);

        if (!suggestionChipsShown) {
          setSuggestionChipsShown(true);
        }
      },
    }),
    [
      engageMessage,
      expand,
      suggestionChipsShown,
      setEngageMessageShown,
      setSuggestionChipsShown,
    ],
  );

  return (
    <div
      dir={direction ?? 'ltr'}
      className={st(classes.root, {
        left: stylesConfig.isLeft,
        sticky: stylesConfig.isSticky,
        nudge:
          settings.get(
            settingsParams.minimizedButtonNudgeAnimation,
            'primary',
          ) === true &&
          (!wasOpened || hasLastUnread),
        hasUnread: hasLastUnread || hasAnyIntro,
        onlyIcon: stylesConfig.showIcon && !stylesConfig.showText,
      })}
      ref={setRef}
      data-hook={MinimizedChatDataHooks.ROOT}
    >
      <NotificationBubble
        shown={shouldShowPopover}
        placement={stylesConfig.isLeft ? 'top-start' : 'top-end'}
        hasShadow={stylesConfig.bubbleHasShadow}
        bubbleClassName={st(classes.bubble)}
        {...(hasLastUnread ? unreadBubbleProps : engageBubbleProps)}
      >
        <>
          {shouldShowSuggestionChipsContainer && (
            <div
              className={st(classes.chipsContainer, {
                left: stylesConfig.isLeft,
                right: !stylesConfig.isLeft,
              })}
            >
              {!shouldShowPopover && (
                <IconButton
                  className={st(classes.closeChipsButton)}
                  onClick={() => {
                    setSuggestionChipsShown(true);
                  }}
                  icon={<CloseSmall />}
                />
              )}

              <ul className={st(classes.chips)}>
                {suggestions!.map((suggestion, index) => {
                  if (suggestion.enabled && suggestion.text) {
                    return (
                      <Tag
                        key={index}
                        className={st(classes.chip)}
                        skin={TagSkin.light}
                        onClick={() => {
                          onClickChip(suggestion.text!);
                        }}
                      >
                        {suggestion.text}
                      </Tag>
                    );
                  }
                  return null;
                })}
              </ul>
            </div>
          )}
          <div
            className={st(classes.buttonContainer, {
              left: stylesConfig.isLeft,
              right: !stylesConfig.isLeft,
            })}
          >
            <div className={st(classes.buttonWrapper)}>
              {stylesConfig.showBorderAnimation && (
                <div
                  className={st(
                    classes.animatedBorderBox,
                    playAnimationStyleState,
                  )}
                />
              )}
              <button
                ref={buttonRef}
                onClick={expand}
                className={st(classes.button, {
                  sticky: stylesConfig.isSticky,
                  hasShadow: stylesConfig.hasShadow,
                  showText: stylesConfig.showText,
                })}
                data-hook={MinimizedChatDataHooks.BUTTON}
                aria-expanded={isOpened}
                aria-label={`${t('app.widget.ariallabel.chatButton')}, ${
                  hasLastUnread && t('app.widget.ariallabel.hasUnreadMessages')
                }, ${!isOnline && t('app.widget.ariallabel.chatUnavailable')}`}
              >
                {stylesConfig.showIcon && iconsDict[stylesConfig.icon]}
                {stylesConfig.showText && (
                  <div className={st(classes.text)}>{buttonText}</div>
                )}
              </button>
            </div>
          </div>
        </>
      </NotificationBubble>
    </div>
  );
};
